import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

import { FleetChecklist, FleetChecklistUpdate, FleetChecklistSummary, FleetStatus, FleetChecklistType } from '../models/fleet-checklist';
import { VehicleValidation } from '../models/vehicle-validation';
import { DynamicField } from '../models/dynamic-field';
import { FleetChecklistApiService } from '../services/fleet-checklist-api.service';
import { ValidationCriterion } from '../models/validation-criterion';

import {FleetChecklistHelper} from '../helpers/fleet-checklist.helper';

@Injectable({
  providedIn: 'root'
})
export class FleetChecklistStoreService {

  private fleetChecklist = new BehaviorSubject<FleetChecklist | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorMessageSubject = new BehaviorSubject<string | null>(null);

  public fleetChecklist$ = this.fleetChecklist.asObservable();
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();
  public errorMessage$: Observable<string | null> = this.errorMessageSubject.asObservable();


  public vehicles$ = this.fleetChecklist$.pipe(
    map(checklist => checklist?.vehicles ?? []),
    distinctUntilChanged()
  );

  constructor(
    private api: FleetChecklistApiService
  ) { }

  initialize(id: string): void {
    this.loadingSubject.next(true);
    this.errorMessageSubject.next(null);
    this.api.getFleetChecklistById(id).then(res => {
      if (!res) {
        this.fleetChecklist.next(null);
        this.errorMessageSubject.next('No se pudieron cargar la información de las validaciones vehiculares.');
        return;
      }
      this.setInitialState(res);
    })
      .catch(error => this.errorMessageSubject.next('No se pudieron cargar las validaciones vehiculares. Intente de nuevo.'))
      .finally(() => this.loadingSubject.next(false));
  }

  setInitialState(checklist: FleetChecklist): void {
    this.fleetChecklist.next(checklist);
  }
  isInitialized(): boolean {
    return !!this.fleetChecklist.getValue()
  }

  async downloadReport(): Promise<boolean> {
    try {
      const response = await this.api.generateReportPDF(
        this.fleetChecklist.getValue()!.id
      );

      if (!response?.body) {
        return false;
      }

      const file = response.body;

      const contentDisposition = response.headers.get('content-disposition');
      const check = this.fleetChecklist.getValue();

      const fileName =
        contentDisposition?.match(/filename="?([^"]+)"?/)?.[1] ??
        `${check!.name} ${check?.parameters?.['validationDate']}.pdf`;

      const url = URL.createObjectURL(file);

      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();

      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  async sendFleetChecklist(withPDF: boolean): Promise<boolean> {
    try {
      return await this.api.sendFleetChecklistEmail(this.fleetChecklist.getValue()!.id, withPDF);
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  // getters
  getChecklistId(): string {
    return this.fleetChecklist.getValue()!.id
  }
  getType(): string {
    return this.fleetChecklist.getValue()!.type
  }
  getSummary(): FleetChecklistSummary | null {
    const checklist = this.fleetChecklist.getValue();
    if (!checklist) {
      return null;
    }

    const ids = checklist.vehicles.map(v => v.id.toString());
    console.log('Store getSummary - vehicles count:', checklist.vehicles.length, 'vehiclesIds:', ids);

    return {
      id: checklist.id,
      name: checklist.name,
      status: checklist.status,
      createdAt: checklist.createdAt,
      type: checklist.type,
      updatedAt: checklist.updatedAt,
      sendetAt: checklist.sendetAt,
      deletedAt: checklist.deletedAt,
      tags: checklist.tags,
      fields: checklist.fields,
      vehiclesIds: ids,
      parameters: checklist.parameters
    }
  }
  getStatus(): FleetStatus | null {
    return this.fleetChecklist.getValue()?.status || null
  }
  getDynamicFields(): DynamicField[] {
    return this.fleetChecklist.getValue()?.fields || []
  }
  getValidationCriteria(): ValidationCriterion[] {
    return this.fleetChecklist.getValue()?.criteria || []
  }
  isEditable(): boolean {
    return this.getStatus() == FleetStatus.IN_PROGRESS
  }

  buildTags(type: string, vehicles: VehicleValidation[]): string[] {

    const set = new Set<string>();

    if (type === FleetChecklistType.EXTERNO) {

      const programadas: string[] = [];
      const rutas: string[] = [];

      for (const v of vehicles) {

        const programada = v.fields?.['programada']?.value;
        if (programada) {
          const [h, m] = programada.split(':');
          programadas.push(`${h}:${m}`);
        }

        const ruta = v.fields?.['ruta']?.value;
        if (ruta) {
          rutas.push(String(ruta));
        }
      }

      // agregar primero programadas luego rutas
      [...new Set(programadas)].forEach(v => set.add(v));
      [...new Set(rutas)].forEach(v => set.add(v));
    }

    else if (type === FleetChecklistType.INTERNO) {

      for (const v of vehicles) {

        let allMorning = true;
        let hasAfternoon = false;

        const value = v.fields?.['programada']?.value;
        if (!value) continue;

        const [h, m] = value.split(':').map(Number);
        const totalMinutes = h * 60 + m;
        if (totalMinutes >= 12 * 60) {
          hasAfternoon = true;
          allMorning = false;
        }
        if (allMorning) {
          set.add('Turno Mañana');
        }

        if (hasAfternoon) {
          set.add('Turno Tarde');
        }
      }

    }

    return Array.from(set);
  }

  finishChecklist(): void {
    this.api.finishFleetChecklist(this.fleetChecklist.getValue()!.id).then(() => {
      const current = this.fleetChecklist.getValue();
      if (current) {
        this.fleetChecklist.next({
          ...current,
          status: FleetStatus.COMPLETED
        });
      }
    })
  }
  async updateChecklistStatus(status: FleetStatus): Promise<boolean> {
    let id = this.getChecklistId();
    let updated = await this.api.updateFleetChecklistStatus(id, status);
    if (updated) {
      this.initialize(id);
      return true;
    }
    return false
  }
  updateChecklistVehicles(vehicles: VehicleValidation[]): void {
    const current = this.fleetChecklist.getValue();
    console.log('Store updateChecklistVehicles - current vehicles count:', current?.vehicles?.length, 'new vehicles count:', vehicles?.length);
    if (current) {
      const updatedChecklist = {
        ...current,
        vehicles
      };
      this.fleetChecklist.next(updatedChecklist);
      console.log('Store updated checklist vehicles set to:', updatedChecklist.vehicles.map(v => v.id));
    }
  }
  updateTagsByVehicles(vehicles: VehicleValidation[]): void {
    let type = this.getType();
    let fleetType = FleetChecklistHelper.getFleetType(type);

    let tags = this.buildTags(type, vehicles);
    tags = [fleetType, ...tags];
    let update = {
      id: this.getChecklistId(),
      tags
    }
    this.api.updateFleetChecklist(update).then(() => {
      this.fleetChecklist.next({
        ...this.fleetChecklist.getValue()!,
        tags
      });
    })
  }
}
